import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, Dialog, DialogTitle, DialogContent } from '@material-ui/core'
import clsx from 'clsx'
import { FixedSizeGrid as Grid } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'
import DebouncedInput from '../../../../molecules/DebouncedInput'
import Icon from '../../../../atoms/Icon'
import { ICON_NAMES } from '../../../../../constants'

// Icon item size - defined before useStyles so it can be used in styles
const ICON_SIZE = 80
const ICON_GAP = 12
const ICON_WITH_GAP = ICON_SIZE + ICON_GAP

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: theme.spacing(2)
  },
  searchContainer: {
    marginBottom: theme.spacing(2)
  },
  iconGridContainer: {
    height: '400px',
    width: '100%'
  },
  iconItem: {
    width: `${ICON_SIZE}px`,
    height: `${ICON_SIZE}px`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    border: '2px solid transparent',
    borderRadius: 'var(--border-radius-md)',
    backgroundColor: 'var(--color-surface-050)',
    transition: 'border-color 0.2s, background-color 0.2s',
    padding: theme.spacing(1),
    boxSizing: 'border-box',
    '&:hover': {
      borderColor: 'var(--color-primary-500)',
      backgroundColor: 'var(--color-neutral-050)'
    }
  },
  iconItemSelected: {
    borderColor: 'var(--color-primary-500)',
    backgroundColor: 'var(--color-primary-050)'
  },
  iconDisplay: {
    fontSize: '32px',
    marginBottom: theme.spacing(0.5)
  },
  iconName: {
    fontSize: '10px',
    textAlign: 'center',
    color: 'var(--dai-color-text-dim)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%'
  },
  emptyState: {
    gridColumn: '1 / -1',
    textAlign: 'center',
    padding: '20px',
    color: 'var(--text-color-secondary)'
  }
}))

// Get all icon names as an array - memoized at module level
const ALL_ICON_NAMES = Object.values(ICON_NAMES).filter(Boolean).sort()

function IconPicker ({ open, onClose, selectedIcon, onSelect }) {
  const classes = useStyles()
  const [searchQuery, setSearchQuery] = useState('')

  const filteredIcons = useMemo(() => {
    if (!searchQuery.trim()) return ALL_ICON_NAMES
    const query = searchQuery.toLowerCase()
    return ALL_ICON_NAMES.filter(iconName => iconName.toLowerCase().includes(query))
  }, [searchQuery])

  const handleSearchChange = useCallback((debouncedValue) => {
    setSearchQuery(debouncedValue?.value ?? debouncedValue ?? '')
  }, [])

  const handleIconSelect = useCallback((iconName) => {
    onSelect?.(iconName)
    onClose?.()
  }, [onSelect, onClose])

  // Render individual icon cell - memoized to prevent unnecessary re-renders
  const Cell = useCallback(({ columnIndex, rowIndex, style, data }) => {
    const { filteredIcons, columnCount, selectedIcon, classes, handleIconSelect } = data
    const index = rowIndex * columnCount + columnIndex
    if (index >= filteredIcons.length) return null

    const iconName = filteredIcons[index]
    const isSelected = selectedIcon === iconName

    return (
      <div
        style={{
          ...style,
          marginRight: `${ICON_GAP}px`,
          marginBottom: `${ICON_GAP}px`,
          width: `${ICON_SIZE}px`,
          height: `${ICON_SIZE}px`
        }}
      >
        <div
          className={clsx(classes.iconItem, {
            [classes.iconItemSelected]: isSelected
          })}
          onClick={() => handleIconSelect(iconName)}
          title={iconName}
        >
          <Icon name={iconName} customSize='32px' className={classes.iconDisplay} />
          <div className={classes.iconName}>{iconName}</div>
        </div>
      </div>
    )
  }, [])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      disableEnforceFocus
      disableAutoFocus
      TransitionProps={{ mountOnEnter: true, unmountOnExit: true }}
    >
      <DialogTitle>Select Icon</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.searchContainer}>
          <DebouncedInput
            placeholder='Search icons...'
            onChange={handleSearchChange}
            delay={300}
          />
        </div>
        {filteredIcons.length === 0 ? (
          <div className={classes.emptyState}>No icons found</div>
        ) : (
          <div className={classes.iconGridContainer}>
            <AutoSizer>
              {({ height, width }) => {
                const columnCount = Math.floor(width / ICON_WITH_GAP) || 6
                const rowCount = Math.ceil(filteredIcons.length / columnCount)

                return (
                  <Grid
                    columnCount={columnCount}
                    columnWidth={ICON_WITH_GAP}
                    height={height}
                    rowCount={rowCount}
                    rowHeight={ICON_WITH_GAP}
                    width={width}
                    itemData={{
                      filteredIcons,
                      columnCount,
                      selectedIcon,
                      classes,
                      handleIconSelect
                    }}
                  >
                    {Cell}
                  </Grid>
                )
              }}
            </AutoSizer>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

IconPicker.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  selectedIcon: PropTypes.string,
  onSelect: PropTypes.func
}

IconPicker.defaultProps = {
  open: false,
  onClose: () => {},
  selectedIcon: null,
  onSelect: () => {}
}

export default IconPicker
